<template>
  <!--
    TASK-SF-07 -- DESIGN-05, onboarding step 2 of 3 (academics).
    مقطع -> پایه and مقطع -> رشته are fully data-driven: availableGrades and
    availableStudyFields are derived from the SINGLE nested reference-data
    payload. There is deliberately NO `if (stage === 'high')` anywhere in
    this component -- رشته is simply empty for a مقطع whose
    education_stage_study_fields mapping returns no rows.
  -->
  <form
    class="step-academics"
    @submit.prevent="onNext"
    data-testid="step-academics"
  >
    <p class="onboarding-eyebrow" data-testid="onboarding-eyebrow">
      {{ $t('login.studentOnboarding.eyebrow') }}
    </p>
    <h1 ref="heading" tabindex="-1" class="uc-form-header">
      {{ $t('login.studentOnboarding.step2.heading') }}
    </h1>
    <p class="uc-form-lead">{{ $t('login.studentOnboarding.step2.lead') }}</p>

    <dropdown-list
      id="onboardingStage"
      class="step-academics__field"
      data-testid="onboarding-stage"
      :label="$t('login.studentOnboarding.step2.stageLabel')"
      :disabled-option="$t('login.studentOnboarding.step2.stagePlaceholder')"
      :options="stageOptions"
      :option-display="stageDisplay"
      :value="value.educationStage"
      @input="onStageInput"
    />

    <dropdown-list
      id="onboardingGrade"
      class="step-academics__field"
      data-testid="onboarding-grade"
      :label="$t('login.studentOnboarding.step2.gradeLabel')"
      :disabled="!value.educationStage"
      :disabled-option="
        value.educationStage
          ? $t('login.studentOnboarding.step2.gradePlaceholder')
          : $t('login.studentOnboarding.step2.dependentDisabled')
      "
      :options="gradeOptions"
      :option-display="gradeDisplay"
      :value="value.educationGrade"
      @input="update('educationGrade', $event)"
    />

    <!--
      HUMAN DECISION 4: رشته is rendered ONLY when the production reference
      data returns applicable study fields for the chosen مقطع
      (`availableStudyFields.length > 0`). No disabled-empty control, no
      invented options, and -- as before -- no stage/grade conditional: the
      gate is purely the API array's length.
    -->
    <dropdown-list
      v-if="availableStudyFields.length"
      id="onboardingStudyField"
      class="step-academics__field"
      data-testid="onboarding-study-field"
      :label="$t('login.studentOnboarding.step2.studyFieldLabel')"
      :disabled-option="
        $t('login.studentOnboarding.step2.studyFieldPlaceholder')
      "
      :options="studyFieldOptions"
      :option-display="studyFieldDisplay"
      :value="value.studyField"
      @input="update('studyField', $event)"
    />

    <p
      v-if="dependentsClearedNotice"
      class="uc-form-subtext"
      role="status"
      aria-live="polite"
      data-testid="onboarding-dependents-cleared"
    >
      {{ $t('login.studentOnboarding.step2.dependentsCleared') }}
    </p>

    <div class="onboarding-actions">
      <button
        type="button"
        class="onboarding-actions__back"
        data-testid="onboarding-step2-back"
        @click="$emit('back')"
      >
        {{ $t('login.studentOnboarding.previousButton') }}
      </button>
      <button
        class="uc-form-button"
        type="submit"
        data-testid="onboarding-step2-next"
        :disabled="!canContinue"
      >
        {{ $t('login.studentOnboarding.nextButton') }}
      </button>
    </div>

    <p
      v-if="!canContinue"
      class="onboarding-required-hint"
      role="alert"
      aria-live="polite"
      data-testid="onboarding-required-hint"
    >
      {{ $t('login.studentOnboarding.requiredHint') }}
    </p>
  </form>
</template>

<script>
import DropdownList from '@/components/DropdownList'

export default {
  name: 'StepAcademics',
  components: { DropdownList },
  props: {
    value: {
      type: Object,
      default: () => ({
        educationStage: '',
        educationGrade: '',
        studyField: '',
      }),
    },
    // stages: [ { name, displayName, grades: [...], studyFields: [...] } ]
    // -- taken straight from GET /api/reference/education, never reshaped.
    stages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return { dependentsClearedNotice: false }
  },
  computed: {
    selectedStage() {
      return this.stages.find(s => s.name === this.value.educationStage) || null
    },
    stageOptions() {
      return this.stages.map(s => s.name)
    },
    stageDisplay() {
      return this.toDisplayMap(this.stages)
    },
    availableGrades() {
      return this.selectedStage ? this.selectedStage.grades || [] : []
    },
    availableStudyFields() {
      return this.selectedStage ? this.selectedStage.studyFields || [] : []
    },
    gradeOptions() {
      return this.availableGrades.map(g => g.name)
    },
    gradeDisplay() {
      return this.toDisplayMap(this.availableGrades)
    },
    studyFieldOptions() {
      return this.availableStudyFields.map(f => f.name)
    },
    studyFieldDisplay() {
      return this.toDisplayMap(this.availableStudyFields)
    },
    canContinue() {
      // Required client-side (D-open-4: server-side optional). رشته is only
      // required when the selected مقطع actually offers one.
      if (!this.value.educationStage || !this.value.educationGrade) return false
      if (this.availableStudyFields.length && !this.value.studyField)
        return false
      return true
    },
  },
  mounted() {
    if (this.$refs.heading) this.$refs.heading.focus()
  },
  methods: {
    toDisplayMap(list) {
      return list.reduce((acc, item) => {
        acc[item.name] = item.displayName || item.name
        return acc
      }, {})
    },
    update(key, val) {
      this.$emit('input', { [key]: val })
    },
    onStageInput(stage) {
      // Changing مقطع clears both dependent selects (02_* §9.5). The delta
      // carries the cleared children explicitly.
      const cleared =
        (this.value.educationGrade || this.value.studyField) &&
        stage !== this.value.educationStage
      this.$emit('input', {
        educationStage: stage,
        educationGrade: '',
        studyField: '',
      })
      this.dependentsClearedNotice = !!cleared
    },
    onNext() {
      if (this.canContinue) this.$emit('next')
    },
  },
}
</script>

<style lang="scss" scoped>
.step-academics__field {
  margin-bottom: 16px;
  max-width: none;
}
</style>
